import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetPromoSocialPostsQuery, useLazyGetPromoSocialPostsQuery } from '../../../../redux/api/fymAPI';
import { ErrorAPIResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { GetPromoSocialPostsResponse, SerializedSocialPost } from '../types';

/** The most pages the totals read: 10 pages of 50 posts. More posts than that are left out. */
export const SOCIAL_TOTALS_MAX_PAGES = 10;

interface UseSocialPeriodPostsProps {
  accountId: number;
  days: number;
  /** True when no channel is connected: the request does not go out. */
  skip?: boolean;
}

type PageError = ErrorAPIResult['error'];

/** The pages after the first one, or the error of the page that failed. */
interface LaterPages {
  posts: SerializedSocialPost[];
  /** True when the read stopped at the page cap and the server had more posts. */
  isCapped: boolean;
  error?: PageError;
}

/** The result of the read of the later pages. It is valid only for its `source` and `attempt`. */
interface FinishedRead extends LaterPages {
  /** The first page that gave the first cursor. A new first page makes this result stale. */
  source: GetPromoSocialPostsResponse;
  /** The retry count of the read. A retry makes this result stale. */
  attempt: number;
}

/**
 * Every post of the period with no filter, for the totals row. The server has no route that adds
 * the views and the engagement of a period, so the app reads the pages of
 * `GET promo/social-posts?days` one after the other and adds the numbers itself. The first page is
 * the same request as the table with no filter, so the cache holds one copy.
 *
 * A sum of some pages is not a total. `posts` is undefined until every page is read, `error` is
 * set when a page fails, and `isCapped` is true when the read stopped at the page cap.
 */
export function useSocialPeriodPosts({ accountId, days, skip = false }: UseSocialPeriodPostsProps) {
  const first = useGetPromoSocialPostsQuery({ accountId, days }, { skip });
  const [loadPage] = useLazyGetPromoSocialPostsQuery();
  const [finished, setFinished] = useState<FinishedRead>();
  const [attempt, setAttempt] = useState(0);

  // `currentData` belongs to the current arguments, so a new period or account gives a new object.
  const firstPage = first.currentData;

  useEffect(() => {
    if (firstPage === undefined || firstPage.nextCursor === null) {
      return undefined;
    }
    let isCancelled = false;

    // Each page needs the cursor of the page before it, so the reads cannot run together.
    const readFrom = async (cursor: string, pagesRead: number, found: SerializedSocialPost[]): Promise<LaterPages> => {
      // A retry does not use the cache: the cache can hold the page that failed.
      const result = await loadPage({ accountId, days, cursor }, attempt === 0);
      if (isCancelled) {
        return { posts: found, isCapped: false };
      }
      if (result.data === undefined) {
        return { posts: found, isCapped: false, error: result.error ?? new Error('A page of posts did not load') };
      }
      const posts = [...found, ...result.data.posts];
      const { nextCursor } = result.data;
      if (nextCursor === null) {
        return { posts, isCapped: false };
      }
      if (pagesRead + 1 >= SOCIAL_TOTALS_MAX_PAGES) {
        return { posts, isCapped: true };
      }
      return readFrom(nextCursor, pagesRead + 1, posts);
    };

    readFrom(firstPage.nextCursor, 1, [])
      .catch(
        (error: unknown): LaterPages => ({
          posts: [],
          isCapped: false,
          error: error instanceof Error ? error : new Error(String(error)),
        })
      )
      .then((pages) => {
        if (!isCancelled) {
          setFinished({ ...pages, source: firstPage, attempt });
        }
      })
      .catch(console.error);

    return () => {
      isCancelled = true;
    };
  }, [accountId, attempt, days, firstPage, loadPage]);

  // A result of another first page, or of the read before a retry, is not a result.
  const later =
    finished !== undefined && finished.source === firstPage && finished.attempt === attempt ? finished : undefined;

  const posts = useMemo<SerializedSocialPost[] | undefined>(() => {
    if (firstPage === undefined) {
      return undefined;
    }
    if (firstPage.nextCursor === null) {
      return firstPage.posts;
    }
    // A page that failed gives no total: the sum of the pages before it is too low.
    return later === undefined || later.error !== undefined ? undefined : [...firstPage.posts, ...later.posts];
  }, [firstPage, later]);

  const error = first.error ?? later?.error;

  const { refetch } = first;
  const hasFirstError = first.error !== undefined;
  const retry = useCallback(() => {
    if (hasFirstError) {
      refetch().catch(console.error);
      return;
    }
    setAttempt((previous) => previous + 1);
  }, [hasFirstError, refetch]);

  return {
    posts,
    isLoading: !skip && posts === undefined && error === undefined,
    isCapped: posts !== undefined && later?.isCapped === true,
    error,
    retry,
  };
}
